import React, { FC } from 'react'
import { View, ViewStyle } from 'react-native'

import useTheme from '@common/hooks/useTheme'
import useWindowSize from '@common/hooks/useWindowSize'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'

export const TRACK_PROGRESS_CARD_MAX_WIDTH = 480

type Props = {
  children: React.ReactNode
  // Action buttons rendered at the bottom of the card.
  footer?: React.ReactNode
  cardStyle?: ViewStyle
  // The hairline above the buttons. Off by default: the body now ends in a
  // bounded surface (the status panel or the explorer link), so the rule only
  // added a second horizontal edge a few pixels from the panel's own border.
  // Opt back in only where the footer really needs separating from loose text.
  withFooterDivider?: boolean
}

// Centered "popup" card for the transaction-status screens (Completed / Failed /
// In progress), styled to match the Unlock screen card (see UnlockPanel): same
// 480px max-width, onboardingCardBackground, 24px radius and soft shadow, so the
// success popup reads as the same design-system surface across every render
// target (extension popup, native mobile, mobile-web).
const TrackProgressCard: FC<Props> = ({
  children,
  footer,
  cardStyle,
  withFooterDivider = false
}) => {
  const { theme } = useTheme()
  const { minWidthSize } = useWindowSize()
  // Narrow (mobile-web) viewports keep a tighter 16px inner padding; wide
  // layouts (desktop tab) get the roomier popup. The 16px gutter around the
  // card is the same on both, so it never touches the edges of a phone screen.
  const isMobile = minWidthSize('m')

  return (
    <View style={[flexbox.flex1, flexbox.alignCenter, flexbox.justifyCenter, spacings.ph]}>
      <View
        style={[
          {
            width: '100%',
            maxWidth: TRACK_PROGRESS_CARD_MAX_WIDTH,
            backgroundColor: theme.onboardingCardBackground,
            borderRadius: 24,
            paddingHorizontal: isMobile ? 16 : 32,
            paddingVertical: isMobile ? 16 : 40,
            shadowColor: theme.onboardingCardShadow,
            shadowOffset: { width: 0, height: 12 },
            shadowOpacity: 1,
            shadowRadius: 32,
            elevation: 12
          },
          flexbox.alignCenter,
          cardStyle
        ]}
      >
        <View style={[{ width: '100%' }, flexbox.alignCenter]}>{children}</View>
        {!!footer && (
          <>
            {withFooterDivider && (
              <View
                style={{
                  width: '100%',
                  height: 1,
                  backgroundColor: theme.secondaryBorder,
                  ...spacings.mvLg
                }}
              />
            )}
            <View style={{ width: '100%', marginTop: withFooterDivider ? 0 : 28 }}>{footer}</View>
          </>
        )}
      </View>
    </View>
  )
}

export default React.memo(TrackProgressCard)
